// The ServiceTitan move's review queue. First in groups (migration.reviewGroups), one per table and
// reason, ServiceTitan's records apart from Brook's import's: a year's sign-off counts only
// ServiceTitan's, and Brook's are cleared before go-live. A group opens to its rows, paged
// (migration.reviewGroupRows), with what a person decides for them: leave them all out with one
// reason, or the answer only they have (a cost, a unit, which call, a receipt's tax, who someone is),
// kept on the item and loaded with on the import's next run.
//
// Then every record, one by one (migration.reviewQueue), as before: leave one out on purpose
// (reconciliation then counts it as a known difference), or load a corrected copy: the person edits
// only what's wrong in ServiceTitan's record, and only those fields are sent, laid over the raw
// record, which is kept as it was. One that still doesn't load comes back open with the reason. A
// record that uses an unmapped value resolves itself once the value is mapped. In Blueprint that list
// opens from the groups, so the screen keeps to its sections.

import { useCallback, useEffect, useState } from 'react';
import { client } from '../api.ts';
import { Empty, Loading, Panel, errorText, useQuery } from '../books/ui.tsx';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { KeepSale } from './KeepSale.tsx';
import {
  type GroupRow,
  type PartProps,
  type ReviewGroup,
  changeLines,
  correctedOf,
  correctionOf,
  correctionStart,
  costOk,
  factorOk,
  groupText,
  groupYears,
  holds,
  OPERATE_PERMS,
  REVIEW_KIND,
  tableWords,
  yearLabel,
  type ReviewItem,
} from './model.ts';

type Groups = { groups: ReviewGroup[]; servicetitan: number; brook: number };

export function ReviewQueue(props: PartProps) {
  const groups = useQuery<Groups>('migration.reviewGroups', {});
  const [opened, setOpened] = useState<ReviewGroup | null>(null);
  const [records, setRecords] = useState(0);
  const changed = async () => {
    await groups.reload();
    setRecords((n) => n + 1);
  };
  const g = groups.data;
  // The groups first, then every record one by one under them (in both designs: QA's walks and a
  // person who wants a single record read the flat list).
  return (
    <>
      {!g ? (
        <Panel title="What waits, in groups">
          <Loading error={groups.error} what="the groups" retry={() => void groups.reload()} />
        </Panel>
      ) : opened ? (
        <OpenGroup
          {...props}
          group={opened}
          onBack={() => setOpened(null)}
          onChanged={async () => {
            await changed();
          }}
        />
      ) : (
        <>
          <GroupList
            title="ServiceTitan’s records"
            about={`Each year’s sign-off counts these: ${g.servicetitan.toLocaleString('en-CA')} open.`}
            empty="Nothing of ServiceTitan’s waits: every record parsed and loaded."
            groups={g.groups.filter((x) => x.source === 'servicetitan')}
            onOpen={setOpened}
          />
          {g.brook > 0 && (
            <GroupList
              title="Brook’s import"
              about={`Cleared before go-live, never a year’s sign-off: ${g.brook.toLocaleString('en-CA')} open.`}
              empty=""
              groups={g.groups.filter((x) => x.source === 'brook')}
              onOpen={setOpened}
            />
          )}
        </>
      )}
      <Records {...props} key={records} onChanged={() => void groups.reload()} />
    </>
  );
}

/** One source's groups, on hairlines: what each is, how many, when, and its one action. */
function GroupList({
  title,
  about,
  empty,
  groups,
  onOpen,
}: {
  title: string;
  about: string;
  empty: string;
  groups: ReviewGroup[];
  onOpen: (g: ReviewGroup) => void;
}) {
  return (
    <Panel title={title}>
      {groups.length === 0 ? (
        <Empty>{empty}</Empty>
      ) : (
        <>
          <p className="muted small mig-about">{about}</p>
          <ul className="rows mig-review mig-groups">
            {groups.map((g) => {
              const t = groupText(g);
              const when = groupYears(g.years);
              return (
                <li key={`${g.entity}:${g.reason}`} className="mig-group">
                  <div>
                    <b>{t.title}</b>
                    <span className="muted small block">
                      {g.count.toLocaleString('en-CA')} · {tableWords(g.entity)}
                      {when ? ` · ${when}` : ''}
                    </span>
                    <span className="small block">{t.sentence}</span>
                  </div>
                  <button type="button" className="btn" onClick={() => onOpen(g)}>
                    {t.act ?? 'See them'}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Panel>
  );
}

const PAGE = 50;

/** A group's rows, a page at a time: the next page starts after the last row shown. */
function useGroupRows(group: ReviewGroup) {
  const [rows, setRows] = useState<GroupRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [people, setPeople] = useState<{ id: string; email: string | null; rows: number }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const page = useCallback(
    async (after: string | null) => {
      try {
        const r = await client.call<{
          rows: GroupRow[];
          total: number;
          next: string | null;
          people: { id: string; email: string | null; rows: number }[] | null;
        }>('migration.reviewGroupRows', {
          entity: group.entity,
          reason: group.reason,
          limit: PAGE,
          ...(after ? { after } : {}),
        });
        setRows((was) => (after ? [...was, ...r.rows] : r.rows));
        setTotal(r.total);
        setNext(r.next);
        setPeople(r.people);
        setError(null);
      } catch (e) {
        setError(errorText(e, 'Could not load this group.'));
      }
    },
    [group.entity, group.reason],
  );
  useEffect(() => {
    void page(null);
  }, [page]);
  return { rows, total, next, people, error, more: () => page(next), reload: () => page(null) };
}

function OpenGroup({
  held,
  toast,
  timeZone,
  group,
  onBack,
  onChanged,
}: PartProps & { group: ReviewGroup; onBack: () => void; onChanged: () => Promise<void> }) {
  const t = groupText(group);
  const may = holds(held, OPERATE_PERMS);
  const g = useGroupRows(group);
  const [leaving, setLeaving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [costs, setCosts] = useState<Record<string, string>>({});
  const [factor, setFactor] = useState<Record<string, string>>({});
  const [staff, setStaff] = useState<Record<string, string>>({});
  const team = useQuery<{ staff: { id: string; displayName: string }[] }>(
    'staff.list',
    t.answer === 'person' ? {} : null,
  );
  // ServiceTitan's unmapped records resolve themselves once mapped: they're never left out from here;
  // nor a job's two sales, whose rows say keep both (or one, or neither)
  const mayLeave =
    may && !(group.source === 'servicetitan' && group.kind === 'unmapped') && t.answer !== 'sale';
  const done = async (said: string) => {
    toast(said);
    await Promise.all([g.reload(), onChanged()]);
  };
  const act = async (
    key: string,
    action: string,
    input: Record<string, unknown>,
    said: (n: number) => string,
  ) => {
    setBusy(key);
    try {
      const r = await client.call<{ resolved: number }>(action, input);
      await done(said(r.resolved));
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const given = Object.entries(costs).filter(([, v]) => costOk(v));
  const rowsOf = (n: number) => `${n.toLocaleString('en-CA')} ${n === 1 ? 'row' : 'rows'}`;
  const at = (iso: string) =>
    new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'medium' }).format(
      new Date(iso),
    );
  return (
    <Panel
      title={t.title}
      aside={
        <button type="button" className="linkish mig-link" onClick={onBack}>
          All groups
        </button>
      }
    >
      <p className="small mig-about">
        {t.sentence}{' '}
        <span className="muted">
          {g.total === null ? '' : `${rowsOf(g.total)} · `}
          {tableWords(group.entity)}
        </span>
      </p>
      {g.total === null ? (
        <Loading error={g.error} what="the group" retry={() => void g.reload()} />
      ) : g.total === 0 ? (
        <Empty>Nothing left in this group.</Empty>
      ) : t.answer === 'person' && g.people ? (
        <ul className="rows mig-review mig-people">
          {g.people.map((p) => (
            <li key={p.id} className="mig-person">
              <div>
                <b>Brook’s person {p.id}</b>
                <span className="muted small block">
                  {p.email ? `${p.email} in his tools · ` : ''}
                  {rowsOf(p.rows)} wait on them
                </span>
              </div>
              {may && (
                <div className="mig-answer">
                  <label className="inline-label">
                    Who is this?
                    <select
                      className="field"
                      value={staff[p.id] ?? ''}
                      onChange={(e) => setStaff({ ...staff, [p.id]: e.target.value })}
                    >
                      <option value="">Choose someone</option>
                      {(team.data?.staff ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.displayName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="btn"
                    disabled={!staff[p.id] || busy !== null}
                    onClick={() =>
                      void act(
                        p.id,
                        'migration.reviewWhoIsThis',
                        { person: p.id, staffId: staff[p.id] },
                        (n) => {
                          const who =
                            team.data?.staff.find((s) => s.id === staff[p.id])?.displayName ?? 'them';
                          return `${rowsOf(n)} answered as ${who}'s: they load on the import’s next run.`;
                        },
                      )
                    }
                  >
                    That’s them
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : t.answer === 'sale' ? (
        <ul className="rows mig-review mig-group-rows">
          {g.rows.map(
            (r) =>
              r.sales && (
                <li key={r.key} className="mig-row mig-row-sale">
                  <div>
                    <b>Job {r.sales.job}</b>
                    <span className="muted small block">{yearLabel(r.year)}</span>
                    <KeepSale
                      id={r.ids[0]!}
                      pair={r.sales}
                      may={may}
                      timeZone={timeZone}
                      toast={toast}
                      onDone={async () => {
                        await Promise.all([g.reload(), onChanged()]);
                      }}
                    />
                  </div>
                </li>
              ),
          )}
        </ul>
      ) : (
        <ul className="rows mig-review mig-group-rows">
          {g.rows.map((r) => (
            <li key={r.key} className="mig-row">
              <div>
                <b>
                  {r.material
                    ? (r.material.name ?? `Material ${r.material.id}`)
                    : `${tableWords(group.entity)} ${r.stId}`}
                </b>
                <span className="muted small block">
                  {group.source === 'servicetitan' ? `${yearLabel(r.year)} · ` : ''}
                  {t.answer === 'costs' && r.quantity !== null
                    ? `${r.quantity} ${r.units?.brook ?? ''} on hand, ${r.ids.length} ${r.ids.length === 1 ? 'level' : 'levels'}`
                    : t.answer === 'tax' && r.receipt
                      ? `Receipt $${(r.receipt.totalCents / 100).toFixed(2)}, tax $${(r.receipt.taxCents / 100).toFixed(2)}`
                      : r.message}
                </span>
              </div>
              {may && t.answer === 'costs' && (
                <label className="inline-label mig-cost">
                  Cost per {r.units?.brook ?? 'unit'} ($)
                  <input
                    className="field"
                    inputMode="decimal"
                    value={costs[r.key] ?? ''}
                    onChange={(e) => setCosts({ ...costs, [r.key]: e.target.value })}
                  />
                </label>
              )}
              {may && t.answer === 'units' && (
                <div className="mig-answer">
                  <label className="inline-label">
                    1 {r.units?.brook ?? 'of Brook’s'} is how many{' '}
                    {r.units?.servicetitan ?? 'of ServiceTitan’s'}?
                    <input
                      className="field"
                      inputMode="decimal"
                      value={factor[r.key] ?? ''}
                      onChange={(e) => setFactor({ ...factor, [r.key]: e.target.value })}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn"
                    disabled={!factorOk(factor[r.key] ?? '') || busy !== null}
                    onClick={() =>
                      void act(
                        r.key,
                        'migration.reviewUnitFactor',
                        { id: r.ids[0], perBrookUnit: factor[r.key]!.trim() },
                        () => 'Saved: it loads converted on the import’s next run.',
                      )
                    }
                  >
                    Save
                  </button>
                </div>
              )}
              {may && t.answer === 'call' && r.calls && (
                <div className="mig-choice" role="group" aria-label="Which call it was">
                  {r.calls.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="btn"
                      disabled={busy !== null}
                      onClick={() =>
                        void act(
                          r.key,
                          'migration.reviewPickCall',
                          { id: r.ids[0], callId: c.id },
                          () => `The call at ${at(c.at)} it is: it loads on it on the import’s next run.`,
                        )
                      }
                    >
                      {at(c.at)}
                    </button>
                  ))}
                </div>
              )}
              {may && t.answer === 'tax' && (
                <div className="mig-choice" role="group" aria-label="What its tax was">
                  {(
                    [
                      ['gst_only', '5 % GST'],
                      ['gst_and_pst', '12 % GST and PST'],
                    ] as const
                  ).map(([rate, words]) => (
                    <button
                      key={rate}
                      type="button"
                      className="btn"
                      disabled={busy !== null}
                      onClick={() =>
                        void act(
                          r.key,
                          'migration.reviewTaxRate',
                          { id: r.ids[0], rate },
                          () => `Its tax was ${words}: it loads on the import’s next run.`,
                        )
                      }
                    >
                      {words}
                    </button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {g.next && (
        <button type="button" className="linkish mig-link mig-more" onClick={() => void g.more()}>
          Show {Math.min(PAGE, (g.total ?? 0) - g.rows.length).toLocaleString('en-CA')} more
        </button>
      )}
      {may && (g.total ?? 0) > 0 && (
        <div className="mig-group-acts">
          {t.answer === 'costs' && (
            <button
              type="button"
              className="btn primary"
              disabled={!given.length || busy !== null}
              onClick={() =>
                void act(
                  'costs',
                  'migration.reviewGiveCosts',
                  { costs: given.map(([material, cost]) => ({ material, cost: cost.trim() })) },
                  (n) => `${rowsOf(n)} given a cost: they open at it on the import’s next run.`,
                ).then(() => setCosts({}))
              }
            >
              Give {given.length ? given.length.toLocaleString('en-CA') : 'these'}{' '}
              {given.length === 1 ? 'cost' : 'costs'}
            </button>
          )}
          {mayLeave && (
            <button type="button" className="linkish mig-link" onClick={() => setLeaving(true)}>
              Leave these out
            </button>
          )}
          {group.kind === 'unmapped' && group.source === 'servicetitan' && (
            <span className="muted small">
              Map its value in Mappings; they resolve themselves on the next load.
            </span>
          )}
        </div>
      )}
      {leaving && (
        <LeaveOut
          group={group}
          count={g.total ?? 0}
          onClose={() => setLeaving(false)}
          onDone={async (said) => {
            setLeaving(false);
            await done(said);
          }}
        />
      )}
    </Panel>
  );
}

/** Every open item of a group left out on purpose, with one reason a person types. */
function LeaveOut({
  group,
  count,
  onClose,
  onDone,
}: {
  group: ReviewGroup;
  count: number;
  onClose: () => void;
  onDone: (said: string) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = groupText(group);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ resolved: number }>('migration.reviewLeaveOut', {
        entity: group.entity,
        reason: group.reason,
        note: note.trim(),
      });
      await onDone(`${r.resolved.toLocaleString('en-CA')} left out: ${t.title.toLowerCase()}.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title="Leave these out?"
      outcome={
        group.source === 'brook'
          ? `${count.toLocaleString('en-CA')} of Brook’s rows aren’t brought in. Each keeps this reason, and a later run leaves them out.`
          : `${count.toLocaleString('en-CA')} of ServiceTitan’s records aren’t loaded. Each year’s reconciliation counts them as known differences, not missing records.`
      }
      confirm="Leave them out"
      ready={!!note.trim()}
      busy={busy}
      error={error}
      onConfirm={() => void go()}
      onClose={onClose}
    >
      <p className="small">
        {t.title} · {tableWords(group.entity)}
      </p>
      <label>
        Why, for all of them (required)
        <textarea
          className="field"
          rows={2}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
    </MoneySheet>
  );
}

function Records({ held, toast, timeZone, onChanged }: PartProps & { onChanged: () => void }) {
  const [openOnly, setOpenOnly] = useState(true);
  const q = useQuery<{ items: ReviewItem[] }>('migration.reviewQueue', { openOnly });
  const may = holds(held, OPERATE_PERMS);
  const [resolving, setResolving] = useState<{ item: ReviewItem; how: 'skip' | 'corrected' } | null>(null);
  return (
    <Panel
      title="Review queue"
      aside={
        <label className="checkrow">
          <input type="checkbox" checked={!openOnly} onChange={(e) => setOpenOnly(!e.target.checked)} /> Show
          the resolved ones too
        </label>
      }
    >
      {!q.data ? (
        <Loading error={q.error} what="the review queue" retry={() => void q.reload()} />
      ) : q.data.items.length === 0 ? (
        <Empty>{openOnly ? 'Nothing to review: every record parsed and loaded.' : 'Nothing here yet.'}</Empty>
      ) : (
        <ul className="rows mig-review">
          {q.data.items.map((it) => (
            <li key={it.id} className="mig-item">
              <div>
                <b>
                  {it.entity.replace(/_/g, ' ')} {it.stId}
                </b>{' '}
                <span className={`chip say${it.status === 'open' ? ' bad' : ' ok'}`}>
                  {it.status === 'open' ? (REVIEW_KIND[it.kind] ?? it.kind) : 'Resolved'}
                </span>
                <span className="muted small block">
                  {yearLabel(it.year)} · {it.message}
                  {it.resolution
                    ? ` · ${it.resolution === 'skip' ? 'left out on purpose' : 'a corrected copy loaded'}`
                    : ''}
                </span>
                {it.status === 'open' && it.corrected && !it.sales && (
                  <span className="muted small block">
                    The last corrected copy changed {Object.keys(it.corrected).join(', ')}.
                  </span>
                )}
                {it.sales && (
                  <KeepSale
                    id={it.id}
                    pair={it.sales}
                    may={may}
                    timeZone={timeZone}
                    toast={toast}
                    onDone={async () => {
                      await q.reload();
                      onChanged();
                    }}
                  />
                )}
              </div>
              {may && it.status === 'open' && it.kind !== 'unmapped' && !it.sales && (
                <div className="row-actions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setResolving({ item: it, how: 'skip' })}
                  >
                    Leave it out
                  </button>
                  {it.raw && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setResolving({ item: it, how: 'corrected' })}
                    >
                      Load a corrected copy
                    </button>
                  )}
                </div>
              )}
              {it.status === 'open' && it.kind === 'unmapped' && (
                <span className="muted small">
                  Map its value in Mappings; it resolves itself on the next load.
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {resolving && (
        <Resolve
          item={resolving.item}
          how={resolving.how}
          onClose={() => setResolving(null)}
          onDone={async (said) => {
            toast(said);
            setResolving(null);
            await q.reload();
            onChanged();
          }}
        />
      )}
    </Panel>
  );
}

function Resolve({
  item,
  how,
  onClose,
  onDone,
}: {
  item: ReviewItem;
  how: 'skip' | 'corrected';
  onClose: () => void;
  onDone: (said: string) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const raw = item.raw ?? {};
  const [json, setJson] = useState(() => correctionStart(item));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const edited = how === 'corrected' ? correctedOf(json) : null;
  const change = edited ? correctionOf(raw, edited) : null;
  const corrected = change && Object.keys(change.fields).length ? change.fields : null;
  const ready = !!note.trim() && (how === 'skip' || corrected !== null);
  const resolve = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('migration.resolveReview', {
        id: item.id,
        resolution: how,
        ...(corrected ? { corrected } : {}),
        note: note.trim(),
      });
      await onDone(
        how === 'skip'
          ? `${item.entity.replace(/_/g, ' ')} ${item.stId} is left out: reconciliation counts it as a known difference.`
          : `${item.entity.replace(/_/g, ' ')} ${item.stId}: the corrected copy loads next.`,
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title={how === 'skip' ? 'Leave this record out?' : 'Load a corrected copy?'}
      outcome={
        how === 'skip'
          ? 'It isn’t loaded. The reconciliation counts it as a known difference, not a missing record.'
          : 'The fields you change are laid over ServiceTitan’s record on the next load; the record itself is kept as it was. If it still doesn’t load, it comes back here with the reason.'
      }
      confirm={how === 'skip' ? 'Leave it out' : 'Load the corrected copy'}
      ready={ready}
      busy={busy}
      error={error}
      onConfirm={() => void resolve()}
      onClose={onClose}
    >
      <p className="small">
        {item.entity.replace(/_/g, ' ')} {item.stId}: {item.message}
      </p>
      {how === 'corrected' && (
        <>
          <details className="mig-raw" open>
            <summary>ServiceTitan’s record, as it came</summary>
            <pre className="mono-field">{JSON.stringify(raw, null, 2)}</pre>
          </details>
          <label>
            The record, with your corrections (JSON)
            <span className="muted small block">
              Change only what’s wrong. Whatever you leave as it is stays as ServiceTitan sent it.
            </span>
            <textarea
              className="field mono-field"
              rows={12}
              spellCheck={false}
              value={json}
              onChange={(e) => setJson(e.target.value)}
            />
          </label>
          {!edited ? (
            <p className="error small">
              This isn’t a JSON object yet: check its brackets, commas and quotes.
            </p>
          ) : !corrected ? (
            <p className="muted small">Nothing changed yet: edit the field that’s wrong.</p>
          ) : (
            <div className="small">
              Only these are sent:
              <ul className="mig-changes">
                {changeLines(raw, change!.fields).map((l) => (
                  <li key={l} className="mono-field">
                    {l}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {change && change.removed.length > 0 && (
            <p className="muted small">
              Taking a field out doesn’t remove it: {change.removed.join(', ')}{' '}
              {change.removed.length === 1 ? 'stays' : 'stay'} as ServiceTitan sent{' '}
              {change.removed.length === 1 ? 'it' : 'them'}.
            </p>
          )}
        </>
      )}
      <label>
        Why (required)
        <textarea
          className="field"
          rows={2}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
    </MoneySheet>
  );
}
